<template>
  <MainLayout>

    <!-- -- Hero ----------------------------------------------------------------- -->
    <section class="relative bg-gradient-hero py-7 lg:py-10 overflow-hidden">
      <div class="absolute inset-0 hex-pattern pointer-events-none"></div>
      <div class="absolute left-1/4 top-0 w-[400px] h-[400px] bg-secondary/10 rounded-full blur-[100px] pointer-events-none"></div>
      <!-- Question mark decorations -->
      <div class="absolute right-24 top-12 text-white/5 text-[140px] font-black select-none hidden lg:block leading-none">?</div>
      <div class="absolute left-20 bottom-8 text-white/4 text-[80px] font-black select-none hidden lg:block leading-none">?</div>

      <div class="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <div class="flex justify-center mb-4 animate-fade-up" style="animation-delay:0.1s">
          <div class="flex items-center gap-3">
            <span class="w-8 h-0.5 bg-secondary rounded-full"></span>
            <span class="text-secondary text-xs font-bold uppercase tracking-[0.2em]">Have Questions?</span>
            <span class="w-8 h-0.5 bg-secondary rounded-full"></span>
          </div>
        </div>
        <h1 class="text-5xl sm:text-6xl font-black text-white mb-4 animate-fade-up" style="animation-delay:0.2s">
          Frequently Asked <span class="text-secondary">Questions</span>
        </h1>
        <p class="text-white/60 text-base max-w-lg mx-auto mb-6 animate-fade-up" style="animation-delay:0.3s">
          Everything you need to know about Orel Medical Supplies, our products, services, and processes.
        </p>
        <nav class="flex items-center justify-center gap-2 text-sm text-white/50 animate-fade-up" style="animation-delay:0.4s">
          <RouterLink to="/" class="hover:text-secondary transition-colors duration-200">Home</RouterLink>
          <i class="fa-solid fa-chevron-right text-xs text-white/30"></i>
          <span class="text-white/80">FAQs</span>
        </nav>
      </div>
    </section>

    <!-- -- FAQ Section ---------------------------------------------------------- -->
    <section class="py-20 lg:py-28 bg-white overflow-hidden">
      <div class="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">

        <!-- Header -->
        <div class="text-center mb-14" data-aos="fade-up">
          <div class="flex justify-center">
            <div class="section-label">FAQs</div>
          </div>
          <h2 class="section-title">Looking For Answers?</h2>
          <p class="section-subtitle max-w-lg mx-auto">
            Here are detailed answers to the most common questions about Orel Medical Supplies Ltd.
          </p>
        </div>

        <!-- Category tabs -->
        <div class="flex flex-wrap justify-center gap-2 mb-10" data-aos="fade-up">
          <button
            v-for="cat in categories"
            :key="cat"
            @click="activeCategory = cat"
            :class="[
              'px-4 py-2 rounded-xl text-xs font-bold uppercase tracking-wider transition-all duration-200',
              activeCategory === cat
                ? 'bg-gradient-primary text-white shadow-md'
                : 'bg-primary/8 text-primary hover:bg-primary/15',
            ]"
          >
            {{ cat }}
          </button>
        </div>

        <!-- FAQ Accordion - 2 columns -->
        <div class="grid lg:grid-cols-2 gap-4">
          <div
            v-for="(faq, index) in filteredFaqs"
            :key="index"
            class="bg-white rounded-2xl border transition-all duration-300"
            :class="openIndex === index
              ? 'border-primary/30 shadow-card-lg'
              : 'border-gray-100 shadow-card hover:border-primary/20'"
          >
            <!-- Question -->
            <button
              class="w-full flex items-center justify-between gap-4 px-6 py-5 text-left"
              @click="toggleFaq(index)"
              :aria-expanded="openIndex === index"
            >
              <div class="flex items-center gap-4">
                <div
                  :class="[
                    'w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 text-xs font-black transition-all duration-300',
                    openIndex === index
                      ? 'bg-gradient-primary text-white shadow-md'
                      : 'bg-primary/8 text-primary',
                  ]"
                >
                  {{ String(index + 1).padStart(2, '0') }}
                </div>
                <span
                  :class="[
                    'font-semibold text-sm leading-snug transition-colors duration-200',
                    openIndex === index ? 'text-primary' : 'text-gray-800',
                  ]"
                >
                  {{ faq.question }}
                </span>
              </div>
              <div
                :class="[
                  'w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 transition-all duration-300',
                  openIndex === index
                    ? 'bg-primary text-white rotate-45'
                    : 'bg-gray-100 text-gray-500',
                ]"
              >
                <i class="fa-solid fa-plus text-xs"></i>
              </div>
            </button>

            <!-- Answer -->
            <div
              class="faq-answer px-6"
              :class="{ open: openIndex === index }"
            >
              <div class="pb-5 pl-12">
                <p class="text-gray-500 text-sm leading-relaxed">{{ faq.answer }}</p>
              </div>
            </div>
          </div>
        </div>

        <!-- Still have questions? -->
        <div class="mt-14 bg-gradient-hero rounded-3xl p-8 sm:p-12 text-center relative overflow-hidden" data-aos="fade-up">
          <div class="absolute inset-0 hex-pattern opacity-50 pointer-events-none"></div>
          <!-- EKG line -->
          <svg class="absolute top-0 left-0 w-full opacity-20 pointer-events-none" height="30" viewBox="0 0 800 30" fill="none" preserveAspectRatio="none">
            <polyline points="0,15 150,15 180,15 195,3 210,27 225,8 240,22 255,15 650,15 680,15 695,4 710,26 725,9 740,21 755,15 800,15" stroke="#9AB4C8" stroke-width="1.5" fill="none"/>
          </svg>
          <div class="relative">
            <div class="w-14 h-14 bg-white/10 rounded-2xl flex items-center justify-center mx-auto mb-5">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-7 h-7 text-secondary">
                <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/>
              </svg>
            </div>
            <h3 class="text-2xl sm:text-3xl font-black text-white mb-3">
              Still Have Questions?
            </h3>
            <p class="text-white/65 text-base mb-6 max-w-lg mx-auto">
              Can't find the answer you're looking for? Our team is ready to help - reach out and we'll respond within 24 hours.
            </p>
            <div class="flex flex-col sm:flex-row items-center justify-center gap-4">
              <RouterLink to="/contact-us" class="btn-white">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-4 h-4">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22 6 12 13 2 6"/>
                </svg>
                Contact Our Team
              </RouterLink>
              <a href="tel:+256779266772" class="btn-outline border-white/30 text-white hover:bg-white hover:text-primary">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-4 h-4">
                  <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.81a19.79 19.79 0 01-3.07-8.64A2 2 0 012 .17h3a2 2 0 012 1.72 12.05 12.05 0 00.72 2.92 2 2 0 01-.45 2.11L6.09 7.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.05 12.05 0 002.92.72A2 2 0 0122 13.92v3z"/>
                </svg>
                Call Us Directly
              </a>
            </div>
          </div>
        </div>

      </div>
    </section>

  </MainLayout>
</template>

<script setup>
import { ref, computed } from 'vue'
import MainLayout from './Layout/MainLayout.vue'

const openIndex      = ref(null)
const activeCategory = ref('All')

const toggleFaq = (index) => {
  openIndex.value = openIndex.value === index ? null : index
}

const categories = ['All', 'Products', 'Services', 'Procurement', 'Support']

const faqs = [
  {
    category: 'Products',
    question: 'What types of medical equipment and supplies does Orel Medical Supplies Ltd offer?',
    answer:   'We offer a comprehensive range of premium biomedical products including diagnostic equipment (digital weighing scales, blood pressure monitors, patient monitors), laboratory equipment, hospital furniture, CSSD (Central Sterile Services Department) supplies, and a wide catalog of medical consumables. All products are sourced directly from certified, internationally recognized manufacturers.',
  },
  {
    category: 'Products',
    question: 'How does Orel ensure the quality and safety of its products?',
    answer:   'Every product undergoes a rigorous quality assurance process - pre-shipment inspection, compliance documentation review, and post-delivery verification. We ensure all products meet WHO standards and relevant national regulatory requirements. We maintain comprehensive quality records for every item we supply, and only partner with manufacturers who hold recognized international certifications.',
  },
  {
    category: 'Procurement',
    question: 'Can Orel assist with tender document preparation for medical procurement?',
    answer:   'Yes - this is one of our core specialized services. Our experienced team prepares comprehensive, audit-ready tender documents covering technical specifications, delivery schedules, warranty terms, compliance certifications, and budget frameworks. We enable healthcare facilities and institutions to procure medical supplies efficiently, transparently, and in full regulatory compliance.',
  },
  {
    category: 'Services',
    question: 'Does Orel provide training for healthcare workers on equipment use?',
    answer:   'Yes. We provide hands-on professional training programs covering safe equipment operation, routine maintenance procedures, infection control protocols, and technology integration. Training programs are tailored to your facility\'s specific equipment inventory and workforce needs, and can be conducted on-site at your health facility.',
  },
  {
    category: 'Services',
    question: 'What does Orel\'s Biomedical Consultancy service involve?',
    answer:   'Our biomedical consultancy covers strategic guidance on health facility planning and design, biomedical equipment selection and procurement strategy, workflow optimization, and support for organizations entering the East African healthcare market. Whether you are setting up a new facility, upgrading existing infrastructure, or seeking expert advice on equipment acquisition, our engineers provide evidence-based recommendations aligned with your goals.',
  },
  {
    category: 'Services',
    question: 'Does Orel handle electrical installations for medical equipment?',
    answer:   'Yes. Our certified technicians manage the full electrical installation lifecycle for medical equipment - from initial load assessment and circuit planning through equipment commissioning and final safety testing. Every installation meets the highest national and international electrical safety standards and is fully documented for regulatory purposes.',
  },
  {
    category: 'Support',
    question: 'What after-sales support does Orel provide?',
    answer:   'We offer comprehensive post-sales support including on-site installation and commissioning, operator training, preventive maintenance scheduling, and troubleshooting assistance. Our field technical officers are available to ensure your equipment operates at peak performance throughout its lifecycle. We view after-sales support as an integral part of our service commitment - not an afterthought.',
  },
  {
    category: 'Support',
    question: 'Which areas of Uganda does Orel Medical Supplies serve?',
    answer:   'While headquartered at Rubaga Road, Butikiro Complex, Kampala, we serve healthcare facilities across Uganda - including government hospitals, private clinics, NGOs, mission hospitals, and international organizations. Our growing network of field technical officers provides nationwide coverage, and we coordinate logistics to ensure timely delivery to facilities both in and outside Kampala.',
  },
]

const filteredFaqs = computed(() => {
  if (activeCategory.value === 'All') return faqs
  return faqs.filter(f => f.category === activeCategory.value)
})
</script>
